import { Fragment, useMemo, useRef } from "react";
import { FieldColumnName } from "../../fields/FieldColumnName";
import { FieldColumn } from "../../fields/FieldColumn";
import { FieldFileMetadatum } from "../../fields/FieldFileMetadatum";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { PanelProps } from "../ModalCreateColumn";
import { getColumnAddress } from "pages/gdt/column-headers/ColumnHeaderBase";

export const PanelCreateColumnFileMetadatum = ({
  autoFocus = true,
  isDefinitionEditable = true,
  fields,
  onFieldChange,
}: PanelProps) => {
  const selectedTableId = useDataTableContext((s) => s.selectedTableId);
  const columnsById = useDataTableContext((s) => {
    const allColumns = s.tables
      .flatMap((table) => table.columns)
      .map((column) => [column.id, column] as const);
    return new Map(allColumns);
  });
  const metadatumFieldRef = useRef<HTMLInputElement | null>(null);
  const nameFieldRef = useRef<HTMLInputElement>(null);

  // Parse source file column from column definition
  const fileColumn = useDataTableContext((s) => {
    const table = s.tables.find(({ id }) => id === s.selectedTableId);
    const columnDefinition = fields.definition.value;
    return columnDefinition?.kind === "metadatum"
      ? table?.columns.find(({ id }) => id === columnDefinition.file_column?.id)
      : undefined;
  });

  // Parse metadatum key from column definition
  const metadatumKey = useMemo(() => {
    return fields.definition.value?.kind === "metadatum"
      ? fields.definition.value.metadatum_key
      : undefined;
  }, [fields.definition.value]);

  // Get file type of the source file column
  const fileType = useMemo(() => {
    if (fileColumn?.definition.kind === "file") {
      return fileColumn.definition.file_types[0];
    } else if (fileColumn?.definition.kind === "join") {
      const sourceColumnId = fileColumn.definition.source_column?.id ?? "";
      const sourceColumn = columnsById.get(sourceColumnId);
      if (sourceColumn?.definition.kind === "file") {
        return sourceColumn.definition.file_types[0];
      }
    }
  }, [columnsById, fileColumn]);

  /**
   * Shifts focus to the metadatum field.
   *
   * This is tried multiple times because EuiComboBox (used by FieldColumn)
   * refocuses itself after onChange is called. I believe this is a bug in EUI
   * but I think this is the best option in the absence of a long-term fix.
   */
  const focusMetadatumField = () => {
    const timer = setInterval(() => {
      if (document.activeElement !== metadatumFieldRef.current) {
        metadatumFieldRef.current?.focus();
      } else {
        clearInterval(timer);
      }
    });
  };

  /**
   * Shifts focus to the name field.
   *
   * This is tried multiple times because EuiSuperSelect (used by
   * FieldFileMetadatum) refocuses itself after onChange is called. I believe
   * this is a bug in EUI but I think this is the best option in the absence of
   * a long-term fix.
   */
  const focusNameField = () => {
    const timer = setInterval(() => {
      if (document.activeElement !== nameFieldRef.current) {
        nameFieldRef.current?.focus();
      } else {
        clearInterval(timer);
      }
    });
  };

  return (
    <Fragment>
      <FieldColumn
        autoFocus={autoFocus}
        disabled={!isDefinitionEditable}
        label="File column"
        helpText="Choose a file column to provide the value for this metadatum column."
        filter={{
          includeColumn: (column) => {
            if (column.definition.kind === "file") {
              return true;
            } else if (column.definition.kind === "join") {
              const sourceColumnId = column.definition.source_column?.id ?? "";
              const sourceColumn = columnsById.get(sourceColumnId);
              return sourceColumn?.definition.kind === "file";
            } else {
              return false;
            }
          },
          tableId: selectedTableId,
        }}
        value={fileColumn?.id}
        onChange={(value) => {
          // Update state
          onFieldChange("definition", {
            value: {
              kind: "metadatum",
              file_column: {
                id: value.id,
                kind: value.kind,
              },
              metadatum_key: "",
            },
            isValid: false,
          });

          // Shift focus to metadatum field
          focusMetadatumField();
        }}
      />

      <FieldFileMetadatum
        inputRef={(ref) => (metadatumFieldRef.current = ref)}
        disabled={!isDefinitionEditable || fileColumn === undefined}
        // File metadatum columns can only point data table columns (not
        // analysis table columns) which only ever have one file type
        fileType={fileType}
        value={metadatumKey}
        onChange={(value) => {
          // Update state
          if (fields.definition.value?.kind === "metadatum") {
            onFieldChange("definition", {
              value: {
                kind: "metadatum",
                file_column: fields.definition.value.file_column,
                metadatum_key: value?.key ?? "",
              },
              isValid: value !== undefined,
            });
          }

          // Update default formula
          if (value !== undefined && fileColumn !== undefined) {
            const colAddress = getColumnAddress(fileColumn.order);
            onFieldChange("default_formula", {
              value: `=METADATUM(${colAddress}:${colAddress}, "${value.key}")`,
              isValid: true,
            });
          }

          // Auto-fill name field if not previously edited
          if (value !== undefined && !fields.name.isDirty) {
            onFieldChange("name", {
              value: value.name,
              isValid: true,
              isDirty: false,
            });
          }

          // Shift focus to name field
          focusNameField();
        }}
      />

      <FieldColumnName
        ref={nameFieldRef}
        value={fields.name.value}
        onChange={(field) => {
          onFieldChange("name", {
            value: field.value,
            isValid: field.isValid,
            isDirty: true,
          });
        }}
      />
    </Fragment>
  );
};
